import Microgame from '../engine/Microgame.js';

export default class DodgeGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Évite !`, en: `Dodge!`, es: `¡Esquiva!` },
            controls: { fr: `Glisse de gauche à droite pour déplacer ton personnage`, en: `Swipe left and right to move your character`, es: `Desliza a izquierda y derecha para moverte` },
            objective: { fr: `Ne te fais pas toucher par les obstacles`, en: `Don't get hit by the obstacles`, es: `No te dejes golpear por los obstáculos` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><path d="m14 10-4 4-4-4"/><path d="m10 2 4 4 4-4"/><circle cx="12" cy="19" r="2"/></svg>`,
            actions: [
                { type: 'move', x: 50, y: 85, duration: 200 },
                { type: 'move', x: 25, y: 85, duration: 500 },
                { type: 'move', x: 65, y: 85, duration: 600 },
                { type: 'move', x: 40, y: 85, duration: 400 },
                { type: 'move', x: 75, y: 85, duration: 500 },
                { type: 'move', x: 30, y: 85, duration: 600 },
                { type: 'move', x: 55, y: 85, duration: 400 },
            ],
            loopDuration: 3500
        };
    }
    constructor(engine) {
        super(engine);
        this.player = null;
        this.obstacles = [];

        // --- GAME PARAMETERS ---
        this.duration = 4000; // Base duration to survive
        this.baseSpeed = 350; // Base px per second for falling blocks
        this.baseSpawnRate = 0.35; // Base spawn rate in seconds
        // -----------------------

        this.speed = this.baseSpeed;
        this.spawnRate = this.baseSpawnRate;
        this.spawnTimer = 0;
        this.elapsedTime = 0;
    }

    setup() {
        this.engine.ui.instruction.textContent = "DODGE!";

        // Scale difficulty based on available time
        const timeRatio = this.engine.maxTime / this.duration;
        this.speed = this.baseSpeed / timeRatio; // Less time = faster blocks
        this.spawnRate = this.baseSpawnRate * timeRatio; // Less time = spawn more often (smaller interval)
        this.gracePeriod = 0.4 * timeRatio; // Safe time at start before collisions count

        // Player
        this.player = document.createElement('div');
        this.player.style.position = 'absolute';
        this.player.style.bottom = '50px';
        this.player.style.left = '50%';
        this.player.style.transform = 'translateX(-50%)';
        this.player.style.width = '40px';
        this.player.style.height = '40px';
        this.player.style.background = this.primaryColor;
        this.player.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.player.style.borderRadius = '5px';
        this.container.appendChild(this.player);

        // Events
        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('mousemove', this.onMove);
        this.engine.input.element.addEventListener('touchmove', this.onMove);

        this.spawnObstacle(); // Immediate spawn
    }

    onMove(e) {
        if (!this.active) return;

        let x;
        if (e.touches) {
            x = e.touches[0].clientX;
        } else {
            x = e.clientX;
        }

        // Clamp to screen
        // Clamp to screen boundaries (Prevent Cheating)
        const rect = this.container.getBoundingClientRect();
        const playerWidth = 40; // Defined in setup
        let relX = x - rect.left;

        // Clamp
        if (relX < playerWidth / 2) relX = playerWidth / 2;
        if (relX > rect.width - playerWidth / 2) relX = rect.width - playerWidth / 2;

        this.player.style.left = `${relX}px`;
    }

    update(dt) {
        if (!this.active) return;
        this.elapsedTime += dt / 1000;

        // Challenge Mode Logic: Infinite + Score Accumulation
        if (this.engine.isChallengeMode) {
            // Add score every ~0.5 second (faster)
            this.challengeScoreTimer = (this.challengeScoreTimer || 0) + dt / 1000;
            if (this.challengeScoreTimer >= 0.75) {
                this.engine.challengesCompleted++; // Using this as 'score' storage for infinite
                this.engine.score = this.engine.challengesCompleted; // Sync for display
                this.challengeScoreTimer -= 0.75;

                // Ramp up difficulty
                this.speed += 10;
                this.spawnRate = Math.max(0.1, this.spawnRate * 0.99);

                // Force HUD Update to show score
                this.engine.updateHUD();
            }

            // Disable Engine Timer countdown visual if possible, or just ignore it
            // We rely on "lose()" to end the game.
        }

        // Spawn Obstacles
        this.spawnTimer += dt / 1000;
        if (this.spawnTimer > this.spawnRate) {
            this.spawnObstacle();
            this.spawnTimer = 0;
        }

        // Move Obstacles & Check Collision
        const pRect = this.player.getBoundingClientRect();

        for (let i = this.obstacles.length - 1; i >= 0; i--) {
            const obs = this.obstacles[i];
            const top = parseFloat(obs.style.top);
            const newTop = top + (this.speed * (dt / 1000)); // Speed
            obs.style.top = `${newTop}px`;

            // Collision (Grace period scales with game speed)
            if (this.elapsedTime > this.gracePeriod) {
                const oRect = obs.getBoundingClientRect();
                if (this.isColliding(pRect, oRect)) {
                    this.lose();
                    return;
                }
            }

            // Remove if off screen
            if (newTop > window.innerHeight) {
                obs.remove();
                this.obstacles.splice(i, 1);
            }
        }

        // Standard Win Condition (Time-based for Non-Challenge)
        if (!this.engine.isChallengeMode && this.engine.timer <= 100) {
            this.win();
        }
    }

    spawnObstacle() {
        const obs = document.createElement('div');
        obs.style.position = 'absolute';
        obs.style.top = '-50px';
        // Ensure spawn is within container width
        const maxX = this.container.clientWidth - 50;
        obs.style.left = `${Math.random() * maxX}px`;
        obs.style.width = '50px';
        obs.style.height = '50px';
        obs.style.background = this.secondaryColor;
        obs.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(obs);
        this.obstacles.push(obs);
    }

    isColliding(r1, r2) {
        return !(r2.left > r1.right ||
            r2.right < r1.left ||
            r2.top > r1.bottom ||
            r2.bottom < r1.top);
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('mousemove', this.onMove);
        this.engine.input.element.removeEventListener('touchmove', this.onMove);
    }
}
